import { useQueries } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { Building07, Edit05 } from "@untitledui/icons";
import { useMemo } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { getInitials } from "@/components/base/avatar/utils";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { TabHeader } from "@/components/base/tab-header";
import { useEditSubConSubOrgsForm } from "@/forms/(org-admin)/sub-con/edit-sub-con-sub-orgs/edit-sub-con-sub-orgs.form.hooks";
import type { SubConSubOrgFormRow } from "@/forms/(org-admin)/sub-con/edit-sub-con-sub-orgs/edit-sub-con-sub-orgs.form.types";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	organizationQueryOptions,
	SUB_ORGANIZATION_MAX_PAGE_SIZE,
	type SubOrganizationDisplay,
} from "@/services/organization";
import { SearchSchema } from "../$subConId";

export type SubConSubOrgWithAssignment = SubOrganizationDisplay & {
	isAssigned: boolean;
};

function getEnabledSubOrgIds(
	subContractorAccess: {
		subOrganizationId?: string | null;
		deleted?: boolean;
	}[],
): Set<string> {
	return new Set(
		subContractorAccess
			.filter((access) => !access.deleted && access.subOrganizationId)
			.map((access) => access.subOrganizationId as string),
	);
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId/sub-organizations",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId/sub-organizations",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId/sub-organizations",
	});

	const canUpdateSubContractor = useEntityActionAllowed({
		name: "UpdateSubContractor",
		entityType: "SubContractor",
		entityId: params.subConId,
		enabled: params.subConId.length > 0,
	});
	const canUpdateSubOrganization = useEntityActionAllowed({
		name: "UpdateSubOrganization",
		entityType: "SubOrganization",
		enabled: params.subConId.length > 0,
	});
	const canEditAssignments =
		canUpdateSubContractor.allowed && canUpdateSubOrganization.allowed;
	const isEditMode = search.mode === "edit" && canEditAssignments;
	const isViewMode = !isEditMode;

	const { allSubOrgs, enabledSubOrgs, enabledSubOrgIds, isLoading, isError } =
		useQueries({
			queries: [
				organizationQueryOptions.subOrganizations.list(
					params.orgId,
					1,
					SUB_ORGANIZATION_MAX_PAGE_SIZE,
				),
				organizationQueryOptions.subContractors.detail(
					params.orgId,
					params.subConId,
				),
			],
			combine: (results) => {
				const [allResult, subContractorResult] = results;

				if (allResult.isPending || subContractorResult.isPending) {
					return {
						allSubOrgs: [] as SubConSubOrgWithAssignment[],
						enabledSubOrgs: [] as SubConSubOrgWithAssignment[],
						enabledSubOrgIds: new Set<string>(),
						isLoading: true,
						isError: false,
					};
				}

				if (allResult.isError || subContractorResult.isError) {
					return {
						allSubOrgs: [] as SubConSubOrgWithAssignment[],
						enabledSubOrgs: [] as SubConSubOrgWithAssignment[],
						enabledSubOrgIds: new Set<string>(),
						isLoading: false,
						isError: true,
					};
				}

				const allList = allResult.data ?? [];
				const enabledIds = getEnabledSubOrgIds(
					subContractorResult.data?.subContractorAccess ?? [],
				);

				const allWithAssignment: SubConSubOrgWithAssignment[] = allList.map(
					(subOrg) => ({
						...subOrg,
						isAssigned: subOrg.id ? enabledIds.has(subOrg.id) : false,
					}),
				);

				const enabledWithFlag = allWithAssignment.filter(
					(subOrg) => subOrg.isAssigned,
				);

				return {
					allSubOrgs: allWithAssignment,
					enabledSubOrgs: enabledWithFlag,
					enabledSubOrgIds: enabledIds,
					isLoading: false,
					isError: false,
				};
			},
		});

	const initialFormData: SubConSubOrgFormRow[] = useMemo(
		() =>
			allSubOrgs.map((subOrg) => ({
				subOrganizationId: subOrg.id ?? "",
				subOrganizationName: subOrg.name ?? "Unknown",
				included: subOrg.id ? enabledSubOrgIds.has(subOrg.id) : false,
			})),
		[allSubOrgs, enabledSubOrgIds],
	);

	const { form, resetForm } = useEditSubConSubOrgsForm({
		organizationId: params.orgId,
		subContractorId: params.subConId,
		initialData: initialFormData,
		initialAssignedIds: enabledSubOrgIds,
		onSuccess: () => {
			navigate({
				to: "/org-admin/$orgId/sub-contractors/$subConId/sub-organizations",
				params: { orgId: params.orgId, subConId: params.subConId },
				search: { mode: "view" },
			});
		},
	});

	const handleCancel = () => {
		resetForm();
		navigate({
			to: "/org-admin/$orgId/sub-contractors/$subConId/sub-organizations",
			params: { orgId: params.orgId, subConId: params.subConId },
			search: { mode: "view" },
		});
	};

	return (
		<div>
			<TabHeader
				title={t("org-admin.sub-contractors.subOrganizations.title", {
					subOrganization: t("subOrganization.entityLabel"),
				})}
				descriptionText={
					isEditMode
						? t("org-admin.sub-contractors.subOrganizations.editDescription", {
								subOrganization: t("subOrganization.entityLabel"),
							})
						: t("org-admin.sub-contractors.subOrganizations.description", {
								subOrganization: t("subOrganization.entityLabel"),
							})
				}
				actions={
					search.mode === "view" &&
					canEditAssignments && (
						<Button
							color="secondary"
							size="sm"
							className="p-2 border border-gray-300 border-solid"
							onClick={() => {
								navigate({
									to: "/org-admin/$orgId/sub-contractors/$subConId/sub-organizations",
									params: { orgId: params.orgId, subConId: params.subConId },
									search: { mode: "edit" },
								});
							}}
						>
							<Edit05 size={20} style={{ stroke: "var(--color-gray-400)" }} />
						</Button>
					)
				}
			/>

			{/* Sub-Organizations List */}
			<div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
				{isLoading ? (
					<div className="px-4 py-6 text-center text-sm text-tertiary">
						{t("common.actions.loadingEllipsis")}
					</div>
				) : isError ? (
					<div className="px-4 py-6 text-center text-sm text-error">
						{t("common.actions.errorLoading")}
					</div>
				) : allSubOrgs.length > 0 ? (
					isViewMode ? (
						<>
							{enabledSubOrgs.map((subOrg) => {
								const orgName = subOrg.name ?? "Unknown Organization";
								const initials = getInitials(orgName);

								return (
									<div
										key={subOrg.id}
										className="flex items-center gap-3 px-4 py-3"
									>
										<Avatar
											size="sm"
											initials={initials}
											placeholderIcon={Building07}
										/>
										<span className="text-sm font-medium text-primary">
											{orgName}
										</span>
									</div>
								);
							})}
							{enabledSubOrgs.length === 0 && (
								<div className="px-4 py-6 text-center text-sm text-tertiary">
									{t(
										"org-admin.sub-contractors.subOrganizations.noAssignments",
										{
											subOrganization: t(
												"subOrganization.entityLabel",
											).toLowerCase(),
										},
									)}
								</div>
							)}
						</>
					) : (
						<form.Subscribe
							selector={(state: unknown) =>
								((
									state as {
										values: { subOrganizations?: SubConSubOrgFormRow[] };
									}
								).values.subOrganizations as SubConSubOrgFormRow[]) || []
							}
						>
							{(formSubOrgs: SubConSubOrgFormRow[]) => (
								<div className="flex flex-col">
									{allSubOrgs.map((subOrg) => {
										const itemId = subOrg.id ?? "";
										const itemName = subOrg.name ?? "Unknown Organization";
										const initials = getInitials(itemName);
										const row = formSubOrgs.find(
											(r) => r.subOrganizationId === itemId,
										);
										const included = row?.included ?? false;

										const updateRow = (
											changes: Partial<SubConSubOrgFormRow>,
										) => {
											const currentSubOrgs = form.state.values.subOrganizations;
											const updatedSubOrgs = currentSubOrgs.map((r) =>
												r.subOrganizationId === itemId
													? { ...r, ...changes }
													: r,
											);
											form.setFieldValue("subOrganizations", updatedSubOrgs);
										};

										return (
											<div
												key={itemId}
												className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 last:border-b-0 min-h-[52px]"
											>
												<Checkbox
													isSelected={included}
													onChange={(isSelected: boolean) =>
														updateRow({ included: !!isSelected })
													}
													aria-label={`Include ${itemName}`}
													data-testid={`subcon-suborg-include-${itemId}`}
												/>
												<Avatar
													size="sm"
													initials={initials}
													placeholderIcon={Building07}
												/>
												<span className="text-sm font-medium text-primary">
													{itemName}
												</span>
											</div>
										);
									})}
								</div>
							)}
						</form.Subscribe>
					)
				) : (
					<div className="px-4 py-6 text-center text-sm text-tertiary">
						{t("admin.users.tabs.subOrganizations.dataAccess.empty")}
					</div>
				)}
			</div>

			{/* Edit Mode Footer */}
			{isEditMode && (
				<div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
					<Button color="secondary" size="md" onClick={handleCancel}>
						{t("common.actions.cancelEdits")}
					</Button>
					<form.Subscribe
						selector={(state: unknown) => {
							const s = state as {
								canSubmit: boolean;
								isDirty: boolean;
								isSubmitting: boolean;
							};
							return [s.canSubmit, s.isDirty, s.isSubmitting] as const;
						}}
					>
						{([canSubmit, isDirty, isSubmitting]: readonly [
							boolean,
							boolean,
							boolean,
						]) => (
							<Button
								color="primary"
								size="md"
								data-testid="subcon-suborgs-save-btn"
								isLoading={isSubmitting}
								isDisabled={!canSubmit || !isDirty}
								onClick={() => form.handleSubmit()}
							>
								{t("common.actions.saveEdits")}
							</Button>
						)}
					</form.Subscribe>
				</div>
			)}
		</div>
	);
}
